import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  model,
  signal,
  TemplateRef,
  viewChild,
  viewChildren,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { TemplatePortal } from '@angular/cdk/portal';
import { cuePanorama, cuePlayFill, cueClose, IconComponent, cueArrowLeft, cueArrowRight } from '@cue/ui/icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { translation } from '@cue/translations';
import { ImageWidthPipe, SafeUrlPipe } from '@cue/utilities';
import { computedPrevious } from 'ngxtension/computed-previous';
import { NgxResize } from 'ngxtension/resize';
import { ResponsiveService } from '@cue/responsivity';

export type GalleryItemFileType = 1 | 2 | 3;

export type GalleryItem = {
  src?: string;
  uniqueIdentifier: string;
  matterportUrl?: string;
  type: GalleryItemFileType;
  imagePreferCover: boolean;
  imageObjectPosition: string;
};

@Component({
  selector: 'cue-gallery',
  imports: [IconComponent, TranslocoPipe, SafeUrlPipe, NgxResize, ImageWidthPipe],
  templateUrl: './gallery.component.html',
  styleUrl: './gallery.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class GalleryComponent {
  ICONS = {
    cuePlayFill,
    cuePanorama,
    cueArrowLeft,
    cueArrowRight,
    cueClose,
  };
  items = input<GalleryItem[]>([]);
  openedItem = model<GalleryItem | null>(null);
  previousOpenedItem = computedPrevious(this.openedItem);

  openClose = explicitEffect([this.openedItem], ([openedItem]) => {
    if (openedItem) {
      this.open();
    } else {
      this.close();
    }
  });

  #overlay = inject(Overlay);
  #viewContainerRef = inject(ViewContainerRef);
  #overlayRef?: OverlayRef;
  protected template = viewChild('template', {
    read: TemplateRef,
  });

  createOverlay() {
    if (this.#overlayRef) {
      return;
    }
    this.#overlayRef = this.#overlay.create({
      hasBackdrop: true,
      positionStrategy: this.#overlay.position().global().centerHorizontally().centerVertically(),
      backdropClass: 'cue-lightbox-backdrop',
    });
    this.#overlayRef.backdropClick().subscribe(() => this.openedItem.set(null));
    this.#overlayRef.attach(new TemplatePortal(this.template(), this.#viewContainerRef));
  }

  destroyOverlay() {
    this.#overlayRef?.dispose();
    this.#overlayRef = undefined;
  }

  open() {
    this.createOverlay();
  }
  close() {
    this.openedItem.set(null);
    this.destroyOverlay();
  }

  thumbs = viewChildren('thumb', {
    read: ElementRef,
  });
  itemElements = viewChildren('item', {
    read: ElementRef,
  });

  alreadyOpened = signal(false);
  doThingsOnOpenedItem = explicitEffect([this.openedItem], ([openedItem]) => {
    if (openedItem) {
      const index = this.items().indexOf(openedItem);
      this.scrollIntoView(index, this.alreadyOpened());
      this.alreadyOpened.set(true);
      this.playVideo(index);

      if (this.previousOpenedItem()) {
        const foundPrevious = this.items().find(
          (x) => x.uniqueIdentifier === this.previousOpenedItem()!.uniqueIdentifier
        );
        const previousIndex = foundPrevious ? this.items().indexOf(foundPrevious) : -1;
        if (previousIndex === -1) {
          return;
        }
        this.stopVideo(previousIndex);
      }
    } else {
      this.alreadyOpened.set(false);
    }
  });

  stopVideo(index: number) {
    const videoElement = this.itemElements()[index].nativeElement.querySelector('video') as HTMLVideoElement;
    if (videoElement) {
      videoElement.pause();
      videoElement.currentTime = 0;
    }
  }

  playVideo(index: number) {
    const videoElement = this.itemElements()[index].nativeElement.querySelector('video') as HTMLVideoElement;
    if (videoElement) {
      if (videoElement.readyState >= 3) {
        videoElement.currentTime = 0;
        videoElement.play();
      }
    }
  }

  scheduleWhenWidthReady(callback: () => void) {
    const checkWidth = () => {
      if (this.sliderWidth() > 0) {
        callback();
      } else {
        requestAnimationFrame(checkWidth);
      }
    };
    checkWidth();
  }

  scrollIntoView(index: number, smooth: boolean) {
    const foo = () => {
      this.itemElements()[index].nativeElement.scrollIntoView({
        inline: 'start',
        behavior: smooth ? 'smooth' : 'instant',
      });

      if (this.thumbs()[index]) {
        this.thumbs()[index].nativeElement.scrollIntoView({
          inline: 'center',
          behavior: smooth ? 'smooth' : 'instant',
        });
      }
    };

    this.scheduleWhenWidthReady(foo);
  }

  selectImage(item: GalleryItem, e: Event) {
    e.stopPropagation();
    this.openedItem.set(item);
  }

  moveLeft(e) {
    e.stopPropagation();
    const openedItemIndex = this.items().indexOf(this.openedItem()!);
    if (openedItemIndex > 0) {
      this.openedItem.set(this.items()[openedItemIndex - 1]);
    } else {
      this.openedItem.set(this.items()[this.items().length - 1]);
    }
  }

  moveRight(e) {
    e.stopPropagation();
    const openedItemIndex = this.items().indexOf(this.openedItem()!);
    if (openedItemIndex < this.items().length - 1) {
      this.openedItem.set(this.items()[openedItemIndex + 1]);
    } else {
      this.openedItem.set(this.items()[0]);
    }
  }
  protected readonly translation = translation;
  sliderWidth = signal(0);

  responsiveService = inject(ResponsiveService);
  thumbWidth = computed(() => {
    return this.responsiveService.isMobile() ? 80 : 180;
  });
  onResize($event: { width: number }) {
    this.sliderWidth.set($event.width);
  }

  onScrollEnd($event: Event) {
    const element = $event.target as HTMLElement;
    const scrollLeft = element.scrollLeft;
    const thumbWidth = this.sliderWidth();
    const index = Math.round(scrollLeft / thumbWidth);
    const newIndex = Math.min(Math.max(index, 0), this.items().length - 1);
    const newItem = this.items()[newIndex];
    if (newItem !== this.openedItem()) {
      this.openedItem.set(newItem);
    }
  }
}
